import { useCallback, useEffect, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAutoBrain } from '@/hooks/useAutoBrain';
import useBrains from '@/hooks/useBrains';
import { useCloseModal } from '@/hooks/useCloseModal';
import { selectBrainId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { submitWithTrimming } from '@/util/util';

import AutoBrainError from './AutoBrainError';
import AutoBrainLandingPage from './AutoBrainLandingPage';
import AutoBrainLoading from './AutoBrainLoading';
import AutoBrainResults from './AutoBrainResults';
import { defaultStartingText } from './util';

const ModalAutoBrain = () => {
  const { t } = useTranslation();
  const closeModal = useCloseModal();
  const [result, setResult] = useState(null);
  const {
    isLoading,
    isSubmitting,
    error,
    generatedDialog,
    addDialogToBrain,
    resetResults,
    generateAutoDialog,
  } = useAutoBrain();

  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);

  useEffect(() => {
    if (generatedDialog && !result) {
      setResult(generatedDialog);
    }
  }, [generatedDialog, result]);

  const formMethods = useForm({ mode: 'onChange', defaultValues: { prompt: '' } });
  const {
    formState: { errors },
    control,
  } = formMethods;

  const prompt = useWatch({ control, name: 'prompt' });

  const onSubmit = useCallback(
    ({ prompt }: { prompt: string }) => {
      trackEvent(EventName.ClickSubmitAutoDialogPrompt);
      generateAutoDialog(`${defaultStartingText(brain?.language)}${prompt}`);
    },
    [brain?.language, generateAutoDialog]
  );

  const handleBack = () => {
    if (result) {
      resetResults();
      setResult(null);
    } else {
      closeModal();
    }
  };

  const disableSaveButton =
    prompt.length < 48 - defaultStartingText(brain?.language).length || prompt.length > 1200;

  const hiddenTitle = <DialogTitle className="sr-only">{t('auto_brain.auto_builder')}</DialogTitle>;

  const hasDescription = !error && (Boolean(result) || !isLoading);

  const renderHeader = () => {
    if (error) {
      return hiddenTitle;
    }
    if (result) {
      return (
        <DialogHeader>
          <DialogTitle>{t('auto_brain.dialog_is_ready')}</DialogTitle>
          <DialogDescription>{t('auto_brain.given_description')}</DialogDescription>
        </DialogHeader>
      );
    }
    if (!isLoading) {
      return (
        <DialogHeader>
          <DialogTitle>{t('auto_brain.auto_builder')}</DialogTitle>
          <DialogDescription>{t('auto_brain.auto_builder_subtitle')}</DialogDescription>
        </DialogHeader>
      );
    }
    return hiddenTitle;
  };

  const renderFooter = () => {
    if (isLoading) {
      return null;
    }
    if (error) {
      return (
        <DialogFooter className="justify-center sm:justify-center">
          <Button
            onClick={() => {
              resetResults();
              setResult(null);
            }}
            isLoading={isSubmitting}
          >
            {t('common.try_again')}
          </Button>
        </DialogFooter>
      );
    }
    if (result) {
      return (
        <DialogFooter>
          <Button variant="ghost-neutral" onClick={handleBack}>
            {t('common.back')}
          </Button>
          <Button onClick={addDialogToBrain} isLoading={isSubmitting}>
            {t('common.accept')}
          </Button>
        </DialogFooter>
      );
    }
    return (
      <DialogFooter>
        <Button variant="ghost-neutral" onClick={handleBack}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={submitWithTrimming(formMethods, onSubmit)}
          disabled={disableSaveButton}
          isLoading={isSubmitting}
        >
          {t('common.generate')}
        </Button>
      </DialogFooter>
    );
  };

  // Loading and error are compact states; the landing and result views need the wide shell.
  const contentWidth = isLoading || error ? 'sm:max-w-md' : 'sm:max-w-2xl';

  return (
    <Dialog open onOpenChange={(open) => !open && !isLoading && closeModal()}>
      <DialogContent
        className={contentWidth}
        {...(hasDescription ? {} : { 'aria-describedby': undefined })}
        // Generation can't be aborted, so drop every dismissal path while it runs.
        showCloseButton={!isLoading}
        onPointerDownOutside={(event) => {
          if (isLoading) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (isLoading) {
            event.preventDefault();
          }
        }}
      >
        {renderHeader()}
        {error && <AutoBrainError error={error} />}
        {isLoading && <AutoBrainLoading />}
        {!isLoading && !result && !error && (
          <AutoBrainLandingPage control={control} error={errors?.prompt} />
        )}
        {result && !error && <AutoBrainResults prompt={prompt} data={result} />}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
};

export default ModalAutoBrain;
